import React, { useEffect, useState } from 'react'
import { useKeycloak } from 'react-keycloak'
// eslint-disable-next-line no-unused-vars
import { FbTableColumn, FbTablePagination, FbTableRow } from '@fabricks/ui-components/dist/components/Table/Table'
import { Icons, LoadingIndicator, Table } from '@fabricks/ui-components'
import { get as _get, set as _set } from 'lodash'
import { Skeleton } from '@material-ui/lab'

import { FilterParam, StorageEntryDto } from '../../api/models'
import { useStorageEntries } from '../../services/apiService'
import styled from "styled-components";

interface StorageEntryTableProps {
    initLoadingTime?: number
    searchTerm?: string
    filter?: FilterParam[]
}

const FuelPercent = styled.div`
    position: absolute;
    z-index: 100;
    right: 10px;
    top: 3px;
    color: white;
`

const ProgressHolder = styled.div`
    position: relative;

    &.overflow > .MuiLinearProgress-root {
        background-color: rgb(254, 85, 112) !important;
    }
`

export const StorageEntryTable: React.FC<StorageEntryTableProps> = (props: StorageEntryTableProps) => {
    const { initLoadingTime, filter, searchTerm } = props
    const [initLoading, setInitLoading] = useState((initLoadingTime || 0) > 0)
    const [sort, setSort] = useState({})
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(25)
    const [keycloak] = useKeycloak()

    const { data, loading, error } = useStorageEntries(keycloak.token, {
        pagination: { page, size: pageSize },
        // @ts-ignore
        sort: Object.keys(sort)
            .map(key => {
                return _get(sort, key, null)
            })
            .filter(e => e != null && e.order !== undefined),
        q: searchTerm || null,
        filter: filter || [],
    })

    useEffect(() => {
        return () => {
            setPage(0)
        }
    }, [filter, searchTerm])


    const onSort = (id: string, direction: string | undefined) => {
        let newDirection: string | undefined
        switch (direction) {
            case 'ASC':
                newDirection = 'DESC'
                break
            case 'DESC':
                newDirection = undefined
                break
            default:
                newDirection = 'ASC'
                break
        }

        const temp = { ...sort }
        _set(temp, id, { field: id, order: newDirection })
        setSort(temp)
    }

    const columns: FbTableColumn[] = [
        {
            id: 'zone',
            label: 'Zo',
            sort: {
                active: _get(sort, 'zone.order', undefined) !== undefined,
                onClick: () => onSort('zone', _get(sort, 'zone.order', undefined)),
                direction: _get(sort, 'zone.order', 'asc').toLowerCase(),
            },
        },
        {
            id: 'rack',
            label: 'Ra',
            sort: {
                active: _get(sort, 'rack.order', undefined) !== undefined,
                onClick: () => onSort('rack', _get(sort, 'rack.order', undefined)),
                direction: _get(sort, 'rack.order', 'asc').toLowerCase(),
            },
        },
        { id: 'level', label: 'Lvl' },
        { id: 'position', label: 'Pos' },
        { id: 'section', label: 'Sec' },
        { id: 'currentStock', label: 'Current stock' },
        { id: 'maxStock', label: 'Max stock' },
        { id: 'avg3Month', label: 'Avg (3 Month)' },
        { id: 'avg12Month', label: 'Avg (12 Month)' },
        { id: 'direction', label: 'Place direction' },
        { id: 'material', label: 'Material' },
        { id: 'customerGroup', label: 'Picking group' },
        { id: 'boxType', label: 'Box type' },
        { id: 'isOverflow', label: 'Overflow', renderValue: (isOverflow: boolean) => {
            if (isOverflow) {
                return (<div className="indicator_check" style={{ color: 'rgb(144, 238, 2)', cursor: 'pointer' }}><Icons.Check /></div>)
            }
            return (<div style={{ cursor: 'pointer' }}><Icons.Remove /></div>)
        }},
        {
            id: 'fuelLevel', label: 'Filling level', minWidth: 200,
            sort: {
                active: _get(sort, 'fuelLevel.order', undefined) !== undefined,
                onClick: () => onSort('fuelLevel', _get(sort, 'fuelLevel.order', undefined)),
                direction: _get(sort, 'fuelLevel.order', 'asc').toLowerCase(),
            },
            renderValue: (value: any) => {
                // eslint-disable-next-line no-restricted-globals
                if (!value || isNaN(value)) {
                    return 'Invalid or insufficient data'
                }

                if (value < 0) {
                    return 'The current box and package type combination is not suitable'
                }

                value *= 100

                return (
                    <ProgressHolder style={{ position: 'relative' }} className={ value > 100 ? "overflow": "no-overflow"}>
                        <LoadingIndicator styles={{ height: 22 }} value={value} variant={"determinate"} />
                        <FuelPercent>{`${parseFloat(value).toFixed(2)}%`}</FuelPercent>
                    </ProgressHolder>)
            }
        },
    ]
    const pagination: FbTablePagination = {
        rowsPerPage: _get(data, 'getEntries.meta.size', pageSize),
        currentPage: _get(data, 'getEntries.meta.offset', page),
        count: _get(data, 'getEntries.meta.count', 0),
        onChangeRowsPerPage: event => {
            setPage(0)
            setPageSize(parseInt(event.target.value, 10))
        },
        onChangePage: (event, newPage) => setPage(newPage),
    }

    const rows: FbTableRow[] = _get(data, 'getEntries.data', []).map((entry: StorageEntryDto) => {
        return {
            entry: entry.uuid,
            zone: entry.zone,
            rack: entry.rack,
            level: entry.level,
            position: entry.position,
            section: entry.section,
            currentStock: entry.currentStock,
            maxStock: entry.maxStock,
            avg3Month: entry.avg3Month,
            avg12Month: entry.avg12Month,
            customerGroup: _get(entry.material, "customerGroup"),
            material: _get(entry.material, "number"),
            boxType: _get(entry.boxType, "name"),
            direction: entry.direction,
            fuelLevel: entry.fuelLevel,
            isOverflow: entry.isOverflow,
        }
    })

    if (initLoading || loading) {
        setTimeout(() => setInitLoading(false), initLoadingTime)
        return (
            <>
                <Skeleton variant="rect" animation="wave" width="100%" height={30} style={{ marginBottom: '5px' }} />
                <Skeleton variant="rect" animation="wave" width="100%" height={30} style={{ marginBottom: '5px' }} />
                <Skeleton variant="rect" animation="wave" width="100%" height={30} style={{ marginBottom: '5px' }} />
                <Skeleton variant="rect" animation="wave" width="100%" height={30} style={{ marginBottom: '5px' }} />
                <Skeleton variant="rect" animation="wave" width="100%" height={30} style={{ marginBottom: '5px' }} />
            </>
        )
    }

    if (_get(data, 'getEntries.error', null) != null) {
        return (
            <div style={{ textAlign: 'center' }}>
                <Icons.Error color="primary" style={{ fontSize: '45px' }} />
                <p>
                    {_get(data, 'getEntries.error.message', '')}
                    {_get(data, 'getEntries.error.code', '')}
                </p>
            </div>
        )
    }

    if (error) {
        return (
            <div style={{ textAlign: 'center', color: 'red' }}>
                <Icons.Error color="error" style={{ fontSize: '45px' }} />
                <div>An unknown error has occurred</div>
            </div>
        )
    }

    return (
        <Table
            columns={columns}
            pagination={pagination}
            rows={rows}
            onRowClick={(uuid: string) => console.log(`Clicked row with uuid ${uuid}`)}
        />
    )
}

export default StorageEntryTable
